import { attr, belongsTo } from "@ember-data/model";
import type Patient from "parog-web/models/patient";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import type Performer from "parog-web/models/performer";
import tWithDefault from "parog-web/utils/t-with-default";
import JournalEvent from "./refunded-journal-event";

//---- Automatically generated, don't update this part ----

type RiskStringEnum =
  | "MALADIE"
  | "MATERNITE"
  | "ACCIDENT"
  | "TRAVAIL"
  | "PREVENTION";

type ExemptionStringEnum = "ALD";

type ProcedureSourceConceptStringEnum = "CCAM" | "NGAP" | "DMP";

/**
 * Describe "RD" (actes de radiologie) reimbursement.
 * Property descriptions are sometimes copy/pasted from the DMP specification.
 *
 * @see sel-mp-005_hr-integre-lps_v-1.9.pdf for details.
 *
 * The DMP feed provides a list of "execution", or reimbursement, each execution
 * has a list of "Acte Facturé" or "Prestation facturée".
 *
 * The medical-act-refunded represents one "Acte facturé" or "Prestation
 * facturée". They are represented as OMOP PROCEDURE_OCCURRENCEs.
 */
export default class MedicalImagingRefundedJournalEvent extends JournalEvent {
  static override type = "medical-imaging-refunded-journal-event";

  @attr() declare hospitalisation: any;

  /**
   * Nature du risque au titre duquel la prestation est prise en charge.
   * Valeurs possibles : «MALADIE», «MATERNITE», «ACCIDENT», «TRAVAIL»,
   * «PREVENTION»
   */
  @attr("string") declare risk: RiskStringEnum;

  /**
   * Valeur = «ALD» si présente
   */
  @attr("string") declare exemption: ExemptionStringEnum;

  /**
   * CCAM for "Acte facturé"
   * NGAP for "Prestation facturée"
   */
  @attr("string")
  declare procedureSourceConcept: ProcedureSourceConceptStringEnum;

  /**
   * The value depends on the concept.
   */
  @attr("string") declare procedureSourceValue: string;

  /**
   * Libellé de l'acte ou de la série d'actes.
   *
   * En CCAM, on prend le libellé court. Exemple : " panst chir brûlure sf
   * tête/moins 1% corps»
   *
   * En NGAP, on utilise le même libellé que la norme B2
   */
  @attr("string") declare procedureSourceLabel: string;

  /**
   * Uniquement pour les prestation facturées
   * Cotation de l’acte. Valeur par défaut = «1»
   */
  @attr("number") declare coefficient: number;

  @attr("performer") declare performer: Performer;

  static override validations(intl: IntlService) {
    return {
      reimbursementsRetrievedJournalEvent: validatePresence({
        presence: true,
        description: intl.t(
          "validations.medical-imaging-refunded-journal-event.reimbursements-retrieved-journal-event"
        )
      }),
      procedureSourceConcept: [
        validatePresence({
          presence: true,
          description: intl.t(
            "validations.medical-imaging-refunded-journal-event.procedure-source-concept"
          )
        }),
        validateInclusion({
          list: ["CCAM", "NGAP", "DMP"],
          description: intl.t(
            "validations.medical-imaging-refunded-journal-event.procedure-source-concept"
          )
        })
      ],
      procedureSourceValue: validatePresence({
        presence: true,
        description: intl.t(
          "validations.medical-imaging-refunded-journal-event.procedure-source-value"
        )
      }),
      procedureSourceLabel: validatePresence({
        presence: true,
        description: intl.t(
          "validations.medical-imaging-refunded-journal-event.procedure-source-label"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.medical-imaging-refunded-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      coefficient: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.medical-imaging-refunded-journal-event.coefficient"
        )
      }),
      risk: validateInclusion({
        list: ["MALADIE", "MATERNITE", "ACCIDENT", "TRAVAIL", "PREVENTION"],
        allowBlank: true,
        description: intl.t(
          "validations.medical-imaging-refunded-journal-event.risk"
        )
      }),
      exemption: validateInclusion({
        list: ["ALD"],
        allowBlank: true,
        description: intl.t(
          "validations.medical-imaging-refunded-journal-event.exemption"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "medicalImagingRefundedJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "medical-imaging-refunded-journal-event": MedicalImagingRefundedJournalEvent;
  }
}
